import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Plus } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const purchasingAPI = {
  getPurchasingOrgs: () => axios.get(`${API_BASE}/api/purchasing-management/purchasing-organizations`),
  createPurchasingOrg: (data) => axios.post(`${API_BASE}/api/purchasing-management/purchasing-organizations`, data),
  updatePurchasingOrg: (id, data) => axios.put(`${API_BASE}/api/purchasing-management/purchasing-organizations/${id}`, data),
  deletePurchasingOrg: (id) => axios.delete(`${API_BASE}/api/purchasing-management/purchasing-organizations/${id}`),
  
  getPurchasingGroups: () => axios.get(`${API_BASE}/api/purchasing-management/purchasing-groups`),
  createPurchasingGroup: (data) => axios.post(`${API_BASE}/api/purchasing-management/purchasing-groups`, data),
  updatePurchasingGroup: (id, data) => axios.put(`${API_BASE}/api/purchasing-management/purchasing-groups/${id}`, data),
  deletePurchasingGroup: (id) => axios.delete(`${API_BASE}/api/purchasing-management/purchasing-groups/${id}`),
  
  getCompanyCodes: () => axios.get(`${API_BASE}/api/company/company-codes`),
  getCurrencies: () => axios.get(`${API_BASE}/api/master-data/currencies`),
  getLanguages: () => axios.get(`${API_BASE}/api/master-data/languages`),
};

const PurchasingManagement = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('purchasing_management');
  const [viewItem, setViewItem] = useState(null);
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') || 'purchasing-organizations';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  
  const [data, setData] = useState({
    purchasingOrgs: [],
    purchasingGroups: [],
  });

  const [companyCodes, setCompanyCodes] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [languages, setLanguages] = useState([]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [selectedCompanyCode, setSelectedCompanyCode] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('Active');

  useEffect(() => {
    setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  useEffect(() => {
    loadData();
    loadForeignKeyData();
  }, []);

  const loadForeignKeyData = async () => {
    try {
      const [ccRes, currRes, langRes] = await Promise.all([
        purchasingAPI.getCompanyCodes(),
        purchasingAPI.getCurrencies(),
        purchasingAPI.getLanguages(),
      ]);
      setCompanyCodes(ccRes.data);
      setCurrencies(currRes.data);
      setLanguages(langRes.data);
    } catch (error) {
      console.error('Failed to load foreign key data:', error);
    }
  };

  const loadData = async () => {
    try {
      const [orgs, groups] = await Promise.all([
        purchasingAPI.getPurchasingOrgs(),
        purchasingAPI.getPurchasingGroups(),
      ]);
      
      setData({
        purchasingOrgs: orgs.data,
        purchasingGroups: groups.data,
      });
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const formValues = Object.fromEntries(formData.entries());

    Object.keys(formValues).forEach(key => {
      if (formValues[key] === '') {
        formValues[key] = null;
      }
    });

    try {
      if (activeTab === 'purchasing-organizations') {
        const payload = {
          purchasing_org_id: formValues.purchasing_org_id,
          name: formValues.name,
          description: formValues.description || null,
          company_code_id: formValues.company_code_id || null,
          address: formValues.address || null,
          language: formValues.language || null,
          currency: formValues.currency || null,
          status: formValues.status,
        };

        if (editingItem) {
          await purchasingAPI.updatePurchasingOrg(editingItem.purchasing_org_id, payload);
          toast.success('Purchasing Organization updated successfully');
        } else {
          await purchasingAPI.createPurchasingOrg(payload);
          toast.success('Purchasing Organization created successfully');
        }
      } else if (activeTab === 'purchasing-groups') {
        const payload = {
          purchasing_group_id: formValues.purchasing_group_id,
          name: formValues.name,
          description: formValues.description || null,
          email: formValues.email || null,
          phone: formValues.phone || null,
          manager_id: formValues.manager_id || null,
          status: formValues.status,
        };

        if (editingItem) {
          await purchasingAPI.updatePurchasingGroup(editingItem.purchasing_group_id, payload);
          toast.success('Purchasing Group updated successfully');
        } else {
          await purchasingAPI.createPurchasingGroup(payload);
          toast.success('Purchasing Group created successfully');
        }
      }

      setIsDialogOpen(false);
      setEditingItem(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const resetForm = () => {
    setSelectedCompanyCode('');
    setSelectedCurrency('');
    setSelectedLanguage('');
    setSelectedStatus('Active');
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    
    if (activeTab === 'purchasing-organizations') {
      setSelectedCompanyCode(item.company_code_id || '');
      setSelectedCurrency(item.currency || '');
      setSelectedLanguage(item.language || '');
      setSelectedStatus(item.status);
    } else if (activeTab === 'purchasing-groups') {
      setSelectedStatus(item.status);
    }
    
    setIsDialogOpen(true);
  };

  const getFormFields = () => {
    switch (activeTab) {
      case 'purchasing-organizations':
        return (
          <>
            <div>
              <Label>Purchasing Organization ID</Label>
              <Input 
                name="purchasing_org_id"
                defaultValue={editingItem?.purchasing_org_id}
                required
                disabled={!!editingItem}
                placeholder="e.g., P100, 1000"
                maxLength={10}
              />
            </div>
            <div>
              <Label>Name</Label>
              <Input 
                name="name"
                defaultValue={editingItem?.name}
                required
                placeholder="e.g., India Main Procurement"
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input 
                name="description"
                defaultValue={editingItem?.description}
                placeholder="Long text or details"
              />
            </div>
            <div>
              <Label>Company Code (optional)</Label>
              <Select value={selectedCompanyCode || undefined} onValueChange={setSelectedCompanyCode}>
                <SelectTrigger><SelectValue placeholder="Select Company Code (optional)" /></SelectTrigger>
                <SelectContent>
                  {companyCodes.map(cc => (
                    <SelectItem key={cc.company_code_id} value={cc.company_code_id}>
                      {cc.company_code_id} - {cc.company_code_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" name="company_code_id" value={selectedCompanyCode || ''} />
            </div>
            <div>
              <Label>Address (optional)</Label>
              <Input 
                name="address"
                defaultValue={editingItem?.address}
                placeholder="Office location"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Language (optional)</Label>
                <Select value={selectedLanguage || undefined} onValueChange={setSelectedLanguage}>
                  <SelectTrigger><SelectValue placeholder="Select Language" /></SelectTrigger>
                  <SelectContent>
                    {languages.map(l => (
                      <SelectItem key={l.language_code} value={l.language_code}>
                        {l.language_code} - {l.language_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <input type="hidden" name="language" value={selectedLanguage || ''} />
              </div>
              <div>
                <Label>Currency (optional)</Label>
                <Select value={selectedCurrency || undefined} onValueChange={setSelectedCurrency}>
                  <SelectTrigger><SelectValue placeholder="Select Currency" /></SelectTrigger>
                  <SelectContent>
                    {currencies.map(c => (
                      <SelectItem key={c.currency_code} value={c.currency_code}>
                        {c.currency_code} - {c.currency_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <input type="hidden" name="currency" value={selectedCurrency || ''} />
              </div>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus} required>
                <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="status" value={selectedStatus} />
            </div>
          </>
        );

      case 'purchasing-groups':
        return (
          <>
            <div>
              <Label>Purchasing Group ID</Label>
              <Input 
                name="purchasing_group_id"
                defaultValue={editingItem?.purchasing_group_id}
                required
                disabled={!!editingItem}
                placeholder="e.g., 001, RMT"
                maxLength={10}
              />
            </div>
            <div>
              <Label>Name</Label>
              <Input 
                name="name"
                defaultValue={editingItem?.name}
                required
                placeholder="e.g., Raw Materials"
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input 
                name="description"
                defaultValue={editingItem?.description}
                placeholder="Department/commodity/region"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email (optional)</Label>
                <Input 
                  name="email"
                  type="email"
                  defaultValue={editingItem?.email}
                  placeholder="contact@example.com"
                />
              </div>
              <div>
                <Label>Phone (optional)</Label>
                <Input 
                  name="phone"
                  defaultValue={editingItem?.phone}
                  placeholder="+1234567890"
                />
              </div>
            </div>
            <div>
              <Label>Manager ID (optional)</Label>
              <Input 
                name="manager_id"
                defaultValue={editingItem?.manager_id}
                placeholder="e.g., EMP501"
              />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus} required>
                <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" name="status" value={selectedStatus} />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  const getTableColumns = () => {
    const columnsMap = {
      'purchasing-organizations': ['ID', 'Name', 'Company Code', 'Address', 'Language', 'Currency', 'Status', 'Actions'],
      'purchasing-groups': ['ID', 'Name', 'Email', 'Phone', 'Manager', 'Status', 'Actions'],
    };
    return columnsMap[activeTab] || [];
  };

  const getTableRow = (item) => {
    switch (activeTab) {
      case 'purchasing-organizations':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm font-semibold">{item.purchasing_org_id}</td>
            <td className="py-3 px-4 text-sm">{item.name}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.company_code_id || '-'}</td>
            <td className="py-3 px-4 text-xs">{item.address || '-'}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.language || '-'}</td>
            <td className="py-3 px-4 font-mono text-xs">{item.currency || '-'}</td>
            <td className="py-3 px-4 text-xs">
              <span className={`px-2 py-1 rounded ${
                item.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {item.status}
              </span>
            </td>
          </>
        );
      case 'purchasing-groups':
        return (
          <>
            <td className="py-3 px-4 font-mono text-sm font-semibold">{item.purchasing_group_id}</td>
            <td className="py-3 px-4 text-sm">{item.name}</td>
            <td className="py-3 px-4 text-xs">{item.email || '-'}</td>
            <td className="py-3 px-4 text-xs">{item.phone || '-'}</td>
            <td className="py-3 px-4 text-xs">{item.manager_id || '-'}</td>
            <td className="py-3 px-4 text-xs">
              <span className={`px-2 py-1 rounded ${
                item.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {item.status}
              </span>
            </td>
          </>
        );
      default:
        return null;
    }
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case 'purchasing-organizations':
        return data.purchasingOrgs;
      case 'purchasing-groups':
        return data.purchasingGroups;
      default:
        return [];
    }
  };

  const tabs = [
    { id: 'purchasing-organizations', label: 'Purchasing Organizations' },
    { id: 'purchasing-groups', label: 'Purchasing Groups' },
  ];

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Purchasing Management</h2>
          <p className="text-sm text-gray-500 mt-1">Manage purchasing organizations and groups</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setActiveTab(tab.id);
                  setEditingItem(null);
                  setIsDialogOpen(false);
                  resetForm();
                }}
                className={`px-6 py-4 font-medium whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-orange-600 border-b-2 border-orange-600'
                    : 'text-gray-600'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <Card className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-900">
              {tabs.find(t => t.id === activeTab)?.label}
            </h3>
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) {
                setEditingItem(null);
                resetForm();
              }
            }}>
              <DialogTrigger asChild>
                {writable && (
                <Button type="button" onClick={() => {
                  setEditingItem(null);
                  resetForm();
                }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add New
                </Button>
                )}
              </DialogTrigger>
              <DialogContent className="max-h-[85vh] overflow-y-auto max-w-2xl">
                <DialogHeader>
                  <DialogTitle>
                    {editingItem ? 'Edit' : 'Create'} {tabs.find(t => t.id === activeTab)?.label}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {getFormFields()}
                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="flex-1">
                      {editingItem ? 'Update' : 'Create'}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setIsDialogOpen(false);
                        setEditingItem(null);
                        resetForm();
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  {getTableColumns().map((col) => (
                    <th key={`col-${col}`} className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {getCurrentData().length === 0 ? (
                  <tr>
                    <td colSpan={getTableColumns().length} className="text-center py-8 text-gray-500">
                      No records found
                    </td>
                  </tr>
                ) : (
                  getCurrentData().map((item) => (
                    <tr key={item.purchasing_org_id || item.purchasing_group_id} className="border-b border-gray-100 hover:bg-gray-50">
                      {getTableRow(item)}
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <RecordActions
                            doctype="purchasing_management"
                            onView={() => setViewItem(item)}
                            onEdit={() => handleEdit(item)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default PurchasingManagement;
